import React, { useState, useCallback, useEffect, useRef } from 'react';
import { View, StyleSheet, Pressable, Platform } from 'react-native';
import { IconButton, useTheme } from 'react-native-paper';
import MaterialCommunityIcons from '@expo/vector-icons/MaterialCommunityIcons';
import { PANEL_CONSTANTS } from '../constants/panelConstants';

interface ResizablePanelProps {
  children: React.ReactNode;
  defaultWidth?: number;
  minWidth?: number;
  maxWidth?: number;
  position: 'left' | 'right';
  onCollapse?: (collapsed: boolean) => void;
}

export const ResizablePanel: React.FC<ResizablePanelProps> = ({
  children,
  position,
  defaultWidth,
  minWidth,
  maxWidth,
  onCollapse,
}) => {
  const panelDefaults = position === 'left' 
    ? PANEL_CONSTANTS.LEFT_PANEL 
    : PANEL_CONSTANTS.RIGHT_PANEL;

  const [width, setWidth] = useState(defaultWidth ?? panelDefaults.DEFAULT_WIDTH);
  const [isDragging, setIsDragging] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(false);
  const dragStartX = useRef(0);
  const startWidth = useRef(width);
  const theme = useTheme();

  // Use provided values or fallback to constants
  const effectiveMinWidth = minWidth ?? panelDefaults.MIN_WIDTH;
  const effectiveMaxWidth = maxWidth ?? panelDefaults.MAX_WIDTH;

  const handleDragStart = useCallback((e: any) => {
    if (isCollapsed) return;
    setIsDragging(true);
    const pageX = e.nativeEvent?.touches?.[0]?.pageX ?? e.nativeEvent?.pageX;
    dragStartX.current = pageX;
    startWidth.current = width;
  }, [isCollapsed, width]);

  const handleDragMove = useCallback((e: any) => {
    if (!isDragging) return;
    e.preventDefault?.();
    const pageX = e.nativeEvent?.touches?.[0]?.pageX ?? e.nativeEvent?.pageX;
    const delta = pageX - dragStartX.current;
    let newWidth = position === 'left' 
      ? startWidth.current + delta
      : startWidth.current - delta;
    newWidth = Math.max(effectiveMinWidth, Math.min(effectiveMaxWidth, newWidth));
    setWidth(newWidth);
  }, [isDragging, effectiveMinWidth, effectiveMaxWidth, position]);

  const handleDragEnd = useCallback(() => {
    setIsDragging(false);
  }, []);

  useEffect(() => {
    if (isDragging) {
      const handleMouseMove = (e: MouseEvent) => {
        handleDragMove({ nativeEvent: e });
      };
      const handleMouseUp = () => {
        handleDragEnd();
      };

      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);

      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isDragging, handleDragMove, handleDragEnd]);

  useEffect(() => {
    return () => {
      if (isDragging) {
        setIsDragging(false);
      }
    };
  }, [isDragging]);

  const toggleCollapse = () => {
    setIsCollapsed(!isCollapsed);
    onCollapse?.(!isCollapsed);
  };

  return (
    <View style={[
      styles.container, 
      { 
        width: isCollapsed ? 40 : width,
        maxHeight: '100%',
      }
    ]}>
      <View style={[
        styles.content, 
        { 
          backgroundColor: theme.colors.elevation.level1,
          height: '100%',
          overflow: 'auto',
        }
      ]}>
        {!isCollapsed && children}
        <Pressable
          onPress={toggleCollapse}
          style={[
            styles.collapseButton,
            position === 'left' ? styles.collapseButtonLeft : styles.collapseButtonRight,
          ]}
        >
          <MaterialCommunityIcons
            name={position === 'left' 
              ? (isCollapsed ? 'chevron-right' : 'chevron-left')
              : (isCollapsed ? 'chevron-left' : 'chevron-right')
            }
            size={20}
            color={theme.colors.onSurfaceVariant}
          />
        </Pressable>
        {!isCollapsed && (
          <Pressable
            onTouchStart={handleDragStart}
            onTouchMove={handleDragMove}
            onTouchEnd={handleDragEnd}
            onMouseDown={handleDragStart}
            style={[
              styles.resizeHandle,
              position === 'left' ? styles.resizeHandleLeft : styles.resizeHandleRight,
              { 
                backgroundColor: isDragging ? theme.colors.primary : theme.colors.surfaceVariant,
                ...(Platform.OS === 'web' ? { cursor: 'col-resize' } : {})
              }
            ]}
          >
            <View 
              style={[
                styles.resizeHandleIndicator,
                { backgroundColor: theme.colors.surfaceVariant }
              ]}
            >
              <MaterialCommunityIcons
                name="drag-vertical"
                size={16}
                color={isDragging ? theme.colors.onPrimary : theme.colors.onSurfaceVariant}
                style={styles.dragIcon}
              />
            </View>
          </Pressable>
        )}
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    position: 'relative',
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    minHeight: 0, // Important for flex child
  },
  content: {
    flex: 1,
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
  },
  collapseButton: {
    position: 'absolute',
    top: '50%',
    marginTop: -12, 
    width: 24,
    height: 24,
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 2,
  },
  collapseButtonLeft: {
    right: 8,
  },
  collapseButtonRight: {
    left: 8,
  },
  resizeHandle: {
    position: 'absolute',
    top: 0,
    width: 8,
    height: '100%',
    zIndex: 3,
    alignItems: 'center',
    justifyContent: 'center',
  },
  resizeHandleLeft: {
    right: 0,
  },
  resizeHandleRight: {
    left: 0,
  },
  resizeHandleIndicator: {
    width: '100%',
    height: '100%',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'rgba(0, 0, 0, 0.05)',
  },
  dragIcon: {
    opacity: 0.6,
  },
});
